<template>
  <div class = "file-explorer-container"
       :style = "{ height: height ? height : '1000px' }"
  >
    <ToggleAndMobileButtons
      :is-treeview                = "isTreeview"
      @toggle-treeview            = "toggleTreeview"
      @file-explorer-grid-columns = "fileExplorerGridTemplateColumns = $event"
      @close                      = "$emit('close')"
    ></ToggleAndMobileButtons>

    <div
      style  = "display: grid; overflow: hidden; height: 100%; margin-top: 10px"
      :style = "{ gridTemplateColumns: fileExplorerGridTemplateColumns }"
    >
      <div class = "treeview-container">
        <Treeview
          :treeview-key     = "treeviewKey"
          :height           = "height"
          :is-treeview      = "isTreeview"
          :items            = "items"
          :load-content     = "loadContent"
          @select-directory = "selectDirectory"
        ></Treeview>
      </div>

      <div class = "folder">
        <FolderControls @refresh-folder = "refreshFolder"
                        @paths          = "$emit('paths', $event)"
                        @load-audio     = "loadAudio"
        >
          <template slot = "quickView">
            <slot name = "quickView"></slot>
          </template>
          <template slot = "rightButtons">
            <slot name = "rightButtons"></slot>
          </template>
        </FolderControls>

        <FolderHeader
          :searchEntry                  = "searchEntry"
          :search-entries-subject       = "searchEntriesSubject"
          @directory-up                 = "goDirectoryUp"
          :current-directory-to-display = "currentDirectoryToDisplay"
          @entry                        = "searchEntriesSubject.next($event)"
        ></FolderHeader>

        <FolderTable
          :current-directory            = "currentDirectory"
          :current-directory-to-display = "currentDirectoryToDisplay"
          :itemsPerPage                 = "itemsPerPage"
          :total-entries                = "totalEntries"
          :sort-by-table                = "sortByTable"
          :sort-desc-table              = "sortDescTable"
          :height                       = "height"
          @table-row-clicked            = "onTableRowClicked"
          @update-options               = "updateOptions"
          @load-subtitles               = "loadSubtitles"
          @load-growing-file            = "loadGrowingFile"
        ></FolderTable>
      </div>
    </div>
  </div>
</template>

<script>
import { fileBrowserConnection, getDrives, getFilteredContents, getLocationStatus } from '../../services/file-browser-service';
import { BusService } from '../../services/bus.service';
import { SET_ACTIVE_ITEMS, SET_FILE_TABLE_SORT } from '../../utils/mutation-types';
import { Subject } from 'rxjs';
import { debounceTime } from 'rxjs/operators';
import Treeview from '@/components/file-explorer/Treeview';
import { mapState } from 'vuex';
import FolderControls from '@/components/file-explorer/folder-controls/FolderControls';
import ToggleAndMobileButtons from '@/components/file-explorer/ToggleAndMobileButtons';
import FolderHeader from '@/components/file-explorer/FolderHeader';
import FolderTable from '@/components/file-explorer/FolderTable';
import { PATH_TYPE } from '@/utils/constants';

const DEBOUNCE_TIME_IN_MS = 500;
const UPDATE_FILES_TIME_IN_MS = 2000;

export default {
  name:       'FileExplorer',
  components: {FolderTable, FolderHeader, ToggleAndMobileButtons, FolderControls, Treeview},
  props:      {
    playedVideosList: Array,
    height:           String,
  },
  data: () => ({
    fileExplorerGridTemplateColumns: '300px 1fr',
    items:                           [],
    updateFiles:                     null,
    currentDirectory:                null,
    isTreeview:                      true,
    quickViewMode:                   false,
    totalEntries:                    0,
    itemsPerPage:                    200,
    sortBy:                          'Name',
    sortOrder:                       'Asc',
    sortByTable:                     null,
    sortDescTable:                   null,
    skip:                            null,
    userSubtitlesList:               null,
    userSubtitlesListSubscription:   null,
    treeviewKey:                     0,
    lastActiveItem:                  null,
    searchEntry:                     null,
    searchEntriesSubject:            new Subject(),
    searchResultSubscription:        null,
  }),
  computed: {
    ...mapState(['fileBrowserFilterValues', 'fileBrowserFilterType', 'activeItems']),
    currentDirectoryToDisplay: function () {
      if (
        this.fileBrowserFilterValues.length &&
          this.currentDirectory &&
          this.fileBrowserFilterType === 'include'
      ) {
        let currentDirectoryToDisplay = JSON.parse(JSON.stringify(this.currentDirectory));
        if (this.currentDirectory.items) {
          this.filterFiles(currentDirectoryToDisplay);
        }
        return currentDirectoryToDisplay;
      }
      return this.currentDirectory;
    },
    tableHeight: function () {
      return 'calc(' + this.height + ' - 150px)';
    },
  },
  mounted() {
    this.setTableSortingFromStore();
    this.getDrives();
    this.userSubtitlesListSubscription = BusService.userSubtitlesListSubject.subscribe((list) => {
      this.userSubtitlesList = list;
    });
    this.initHandlers();

    this.searchResultSubscription = this.searchEntriesSubject
      .pipe(debounceTime(DEBOUNCE_TIME_IN_MS))
      .subscribe((searchEntry) => {
        this.searchEntry = searchEntry;
        this.setDirectory(this.currentDirectory.physicalPath, 0);
      });
  },
  destroyed() {
    clearInterval(this.updateFiles);
    this.updateFiles = undefined;
    this.userSubtitlesListSubscription.unsubscribe();
    this.searchResultSubscription.unsubscribe();
  },
  methods: {
    initHandlers: function () {
      fileBrowserConnection.on('LocationStatusUpdate', (locationUpdateStatus) => {
        if (!locationUpdateStatus.isOnline) {
          this.handleDirectoryOffline(locationUpdateStatus.physicalPath);
        } else if (locationUpdateStatus.isOnline) {
          this.loadContent(this.getItemInTreeview(locationUpdateStatus.physicalPath));
        }
      });
    },

    setTableSortingFromStore: function () {
      this.sortByTable = this.$store.state.fileTableSort.sortBy;
      this.sortByDesc = this.$store.state.fileTableSort.sortDesc;
    },

    filterFiles(currentDirectoryToDisplay) {
      let valuesToUpperCase = this.fileBrowserFilterValues.map((value) => {
        return value.toUpperCase();
      });
      let filesSet = new Set(valuesToUpperCase);
      let filesToDisplay = [];
      currentDirectoryToDisplay.items.forEach((file) => {
        if (!file.length) {
          filesToDisplay.push(file);
        } else {
          let dotIndex = file.name.lastIndexOf('.');
          if (filesSet.has(file.name.slice(dotIndex).toUpperCase())) {
            if (file.length) {
              filesToDisplay.push(file);
            }
          }
        }
      });
      currentDirectoryToDisplay.items = filesToDisplay;
    },

    async getDrives() {
      const drives = await getDrives();
      drives.forEach((item) => (item.children = undefined));
      this.items = drives;
      await Promise.all(drives.map(this.loadStatus));
    },

    selectDirectory(activeItemsArray) {
      if (activeItemsArray.length === 0) {
        return;
      }
      this.setDirectory(activeItemsArray[0], 0);

    },
    updateOptions(options) {
      this.setSortingPropertiesByOptions(options);
      this.setDirectory(this.currentDirectory.physicalPath, (options.page - 1) * this.itemsPerPage);
    },

    setSortingPropertiesByOptions(options) {
      this.itemsPerPage = options.itemsPerPage;
      if (!options.sortBy.length && !options.sortDesc.length) {
        this.sortBy = 'Name';
        this.sortOrder = 'Asc';
      } else {
        if (options.sortBy[0] === 'name') this.sortBy = 'Name';
        else if (options.sortBy[0] === 'length') this.sortBy = 'Size';
        else if (options.sortBy[0] === 'lastModified') this.sortBy = 'ModificationDate';
        this.sortOrder = options.sortDesc[0] ? 'Desc' : 'Asc';
      }
      this.$store.commit(SET_FILE_TABLE_SORT, {sortBy: options.sortBy[0], sortDesc: options.sortDesc[0]});
    },

    async setDirectory(physicalPath, skip) {
      clearInterval(this.updateFiles);
      this.updateFiles = undefined;
      await this.setDirectoryFunc(physicalPath, skip);
      if (this.updateFiles === undefined) {
        var updateInProgress = false;
        this.updateFiles = setInterval( async() => {
          if (updateInProgress) return;

          updateInProgress = true;
          await this.setDirectoryFunc(physicalPath, skip);
          updateInProgress = false;
        }, UPDATE_FILES_TIME_IN_MS)
      }
    },

    async setDirectoryFunc(physicalPath, skip) {
      let result = await getFilteredContents(
        physicalPath,
        this.getSearchEntry(this.searchEntry),
        skip,
        this.itemsPerPage,
        this.sortBy,
        this.sortOrder
      );
      if (result.resultKind !== 'Success') {
        this.handleDirectoryOffline(physicalPath);
      } else {
        this.handleDirectoryResultSuccess(result, skip, physicalPath);
        this.lastActiveItem = physicalPath;
        return true;
      }
    },

    handleDirectoryOffline: function (physicalPath) {
      if (this.currentDirectory) this.currentDirectory.disabled = true;
      let item = this.getItemInTreeview(physicalPath);
      item.children = [];
      item.iconDisabled = true;
      this.$store.commit(SET_ACTIVE_ITEMS, []);
      this.refreshTreeviewAndScroll();
    },

    handleDirectoryResultSuccess: function (result, skip, physicalPath) {
      const value = result.entity;
      this.skip = skip;
      this.totalEntries = value.total;
      this.currentDirectory = {
        items: value.items.map((item) => {
          return {
            ...item,
            isItemLoading: false,
            isFileUsed:    this.checkIsFileUsed(item.physicalPath),
          };
        }),
        physicalPath: physicalPath,
      };
      this.sortByTable = this.$store.state.fileTableSort.sortBy;
      this.sortDescTable = this.$store.state.fileTableSort.sortDesc;
    },

    refreshTreeviewAndScroll: function () {
      let treeview = document.getElementsByClassName('treeview')[0];
      if (treeview) {
        let scrollTopTreeview = treeview.scrollTop;

        this.treeviewKey++;

        setTimeout(() => {
          treeview.scrollTop = scrollTopTreeview;
        });
      }
    },

    getItemInTreeview(physicalPath) {
      for (let i = 0; i < this.items.length; i++) {
        let item = recursion(this.items[i], physicalPath);
        if (item) return item;
      }

      function recursion(item, path) {
        if (item.physicalPath === path) {
          return item;
        }
        if (item.children && item.children.length) {
          for (const childItem of item.children) {
            let item = recursion(childItem, path);
            if (item) return item;
          }
        }
      }
    },

    getSearchEntry(searchEntry) {
      if (searchEntry) {
        return '*' + searchEntry + '*';
      }
    },

    checkIsFileUsed(path) {
      return (
        this.playedVideosList.findIndex((value) => {
          return value.path === path;
        }) > -1
      );
    },

    onTableRowClicked(item) {
      if (!item.length) {
        this.searchEntry = '';
        this.setDirectory(item.physicalPath, 0);
      } else {
        this.startItemLoading(item.physicalPath);
        this.$emit('path', item.physicalPath, PATH_TYPE.VIDEO);
      }
    },

    loadGrowingFile(item) {
      this.searchEntry = '';
      if (!item.length) {
        this.setDirectory(item.physicalPath, 0);
      } else {
        this.startItemLoading(item.physicalPath);
        this.$emit('path', item.physicalPath, PATH_TYPE.GROWING);
      }
    },

    startItemLoading(physicalPath) {
      this.currentDirectoryToDisplay.items.forEach((item) => {
        item.isItemLoading = physicalPath === item.physicalPath;
      });
    },

    onVideoLoaded(physicalPath) {
      this.endVideoLoading(physicalPath);
      this.setIsFileUsed(physicalPath);
    },

    endItemLoading() {
      if (this.currentDirectoryToDisplay) {
        this.currentDirectoryToDisplay.items.forEach((item) => {
          item.isItemLoading = false;
        });
      }
    },

    endVideoLoading(physicalPath) {
      if (this.currentDirectoryToDisplay) {
        this.currentDirectoryToDisplay.items.forEach((item) => {
          if (physicalPath === item.physicalPath) {
            item.isItemLoading = false;
          }
        });
      }
    },

    setIsFileUsed(physicalPath) {
      if (this.currentDirectoryToDisplay) {
        this.currentDirectoryToDisplay.items.forEach((value) => {
          if (physicalPath === value.physicalPath) {
            value.isFileUsed = true;
          }
        });
      }
    },

    handleResultNotSuccess(item) {
      item.contentLoading = false;
      item.iconDisabled = true;
      item.children = [];
      this.refreshTreeviewAndScroll();
    },

    handleResultSuccess: function (result, item) {
      const content = result.entity;
      content.items = content.items?.filter((entry) => {
        return !entry.length;
      });
      content.items?.forEach((d) => (d.children = []));

      if (content.items?.length === 0) item.children = [];
      else item.children = content.items;

      item.contentLoading = false;
      item.iconDisabled = false;
      item.files = content.files;
    },

    async loadContent(item) {
      if (item) {
        item.contentLoading = true;
        let result = await getFilteredContents(item.physicalPath);
        if (result.resultKind !== 'Success') {
          this.handleResultNotSuccess(item);
        } else {
          this.handleResultSuccess(result, item);
        }
      }
    },

    async loadStatus(item) {
      if (item) {
        item.contentLoading = true;
        try {
          let result = await getLocationStatus(item.physicalPath);
          if (result.resultKind === 'Success') {
            item.iconDisabled = false;
          } else {
            throw result.resultKind;
          }
        }
        catch (err) {
          item.iconDisabled = true;
        }
        finally {
          item.contentLoading = false;
          this.refreshTreeviewAndScroll();
        }
      }
    },

    loadSubtitles(path) {
      this.startItemLoading(path);
      this.$emit('path', path, PATH_TYPE.SUBTITLES);
    },

    loadAudio() {
      let audioPathsConfig = this.$store.state.audioPathsConfig;
      this.$emit('path', audioPathsConfig, PATH_TYPE.AUDIO);
    },

    toggleTreeview() {
      this.isTreeview = !this.isTreeview;
      if (this.isTreeview) {
        this.fileExplorerGridTemplateColumns = '300px 1fr';
      } else {
        this.fileExplorerGridTemplateColumns = '24px 1fr';
      }
    },
    setQuickViewMode() {
      this.quickViewMode = !this.quickViewMode;
      this.$emit('quickViewMode', this.quickViewMode);
    },
    getLastIndexOf(path) {
      let indexOf = path.lastIndexOf('\\');

      if (indexOf < 0) indexOf = path.lastIndexOf('/');

      if (indexOf > 0 && path[indexOf - 1] == ':') indexOf++;

      return indexOf;
    },
    async goDirectoryUp() {
      let indexOf = this.getLastIndexOf(this.currentDirectory.physicalPath);

      if (indexOf > 0) {
        let directoryUp = this.currentDirectory.physicalPath.slice(0, indexOf);

        let isDirectorySet = await this.setDirectory(directoryUp, 0);
        if (isDirectorySet) {
          this.$store.commit(SET_ACTIVE_ITEMS, [directoryUp]);
        }
      }
    },
    searchEntries() {
      this.setDirectory(this.currentDirectory.physicalPath, 0);
    },
    refreshFolder() {
      this.setDirectory(this.currentDirectory.physicalPath, 0);
    },
  },
};
</script>

<style>
.theme--dark.v-data-table {
  background-color : transparent;
}

.file-explorer-container {
  position : relative;
}

.folder {
  padding-left : 20px;
  overflow     : auto;
}

.treeview-container {
  padding-top : 102px;
}

.treeview {
  width          : 300px;
  font-size      : 14px;
  overflow-y     : auto;
  padding-bottom : 20px;
}

.v-treeview-node {
  cursor : pointer;
}

tbody tr {
  cursor : pointer;
}

@media (max-width : 800px) {
  .folder {
    padding : 0 10px;
  }
}

@media (max-width : 500px) {
  .treeview-container {
    padding-top : 0;
  }
}
</style>
